import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  Search,
  ArrowRight,
  Store,
  Star,
  MapPin,
  BadgeCheck,
  ChevronLeft,
  ChevronRight,
  UserPlus,
  Eye,
  TrendingUp,
  Sparkles,
  Users,
  Compass,
  Target,
  HeartHandshake,
  ShoppingBag,
  Gift,
  Crown,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import VersionSwitcher from "@/components/VersionSwitcher";
import BannerAdRotator from "@/components/BannerAdRotator";
import { slugForCategory } from "@/lib/categoryHeroes";
import { CATEGORY_IMAGES } from "@/lib/categoryImages";
import { resolveLogoUrl } from "@/lib/assetUrl";
import CategoryRail from "@/components/CategoryRail";

function initialsOf(name) {
  if (!name) return "TBC";
  const parts = String(name)
    .replace(/[^a-zA-Z0-9\s]/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "TBC";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

// Businesses explicitly excluded from the homepage Featured rail.
// Match is normalized (lowercase, alphanumeric only) and prefix-based so the
// filter catches variants like "AOS Entertainment LLC" and "The real estate trapper".
const FEATURED_EXCLUDED_NORMALIZED = ["therealestatetrapper", "aosentertainment"];

function normalizeBusinessName(name) {
  return (name || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function isExcludedFromFeatured(business) {
  const n = normalizeBusinessName(business.name);
  return FEATURED_EXCLUDED_NORMALIZED.some((key) => n.startsWith(key));
}

const HERO_IMAGES = [
  {
    url: "https://images.unsplash.com/photo-1739298061709-cfc57e6b620e?w=1200&q=80",
    caption: "Building Community Together",
    description: "Black entrepreneurs networking and collaborating",
  },
  {
    url: "https://images.unsplash.com/photo-1573165706511-3ffde6ef1fe3?w=1200&q=80",
    caption: "Women Leading the Way",
    description: "Black women business professionals in collaboration",
  },
  {
    url: "https://images.unsplash.com/photo-1716399233137-5f18a8e3520d?w=1200&q=80",
    caption: "Men Making Moves",
    description: "Black men entrepreneurs closing deals",
  },
  {
    url: `${process.env.REACT_APP_BACKEND_URL || ""}/api/uploads/categories/hero-dining.jpg`,
    caption: "Dining & Hospitality",
    description: "Black-owned fine dining and hospitality experiences",
  },
  {
    url: "https://images.unsplash.com/photo-1653566031535-bcf33e1c2893?w=1200&q=80",
    caption: "Collaborative Success",
    description: "Black professionals collaborating in business meeting",
  },
  {
    url: "https://images.unsplash.com/photo-1653565685060-e15e492a7fda?w=1200&q=80",
    caption: "Leading & Inspiring",
    description: "Black leaders presenting and inspiring teams",
  },
];

const HeroCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => clearInterval(timer);
    // Mount-once interval; HERO_IMAGES is a module constant and setCurrentIndex
    // uses the functional updater form, so no real deps are needed.
  }, []);

  const goToPrevious = () =>
    setCurrentIndex((prev) => (prev - 1 + HERO_IMAGES.length) % HERO_IMAGES.length);
  const goToNext = () => setCurrentIndex((prev) => (prev + 1) % HERO_IMAGES.length);

  return (
    <div className="relative">
      <div className="absolute -inset-4 bg-tbc-gold/20 rounded-3xl transform rotate-3"></div>
      <div className="relative rounded-2xl overflow-hidden h-[400px] lg:h-[500px]">
        {HERO_IMAGES.map((image, index) => (
          <div
            key={image.url}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === currentIndex ? "opacity-100" : "opacity-0"
            }`}
          >
            <img src={image.url} alt={image.caption} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-tbc-navy/60 via-transparent to-transparent"></div>
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <h3 className="font-heading text-2xl mb-1">{image.caption}</h3>
              <p className="text-white/80 text-sm font-body">{image.description}</p>
            </div>
          </div>
        ))}

        <button
          onClick={goToPrevious}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
          data-testid="v3-carousel-prev"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={goToNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
          data-testid="v3-carousel-next"
        >
          <ChevronRight className="w-6 h-6" />
        </button>

        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2">
          {HERO_IMAGES.map((image, index) => (
            <button
              key={`dot-${image.url}`}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex ? "bg-tbc-gold w-6" : "bg-white/50"
              }`}
              data-testid={`v3-carousel-dot-${index}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default function LandingPageV3() {
  const { user, login } = useAuth();
  const [categories, setCategories] = useState([]);
  const [featuredBusinesses, setFeaturedBusinesses] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [locationQuery, setLocationQuery] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, bizRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/businesses?limit=6`),
        ]);
        setCategories(catRes.data.categories || []);
        setFeaturedBusinesses(bizRes.data.businesses || []);
      } catch (error) {
        if (process.env.NODE_ENV !== "production") console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery.trim()) params.set("search", searchQuery);
    if (locationQuery.trim()) params.set("location", locationQuery);
    window.location.href = `/directory?${params.toString()}`;
  };

  const handleListBusiness = () => {
    if (user) {
      window.location.href = "/dashboard";
    } else {
      login();
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page-v3">
      <Navbar />

      {/* ===================== HERO (OWNER-FIRST) ===================== */}
      <section className="relative min-h-[85vh] flex items-center">
        <div className="absolute inset-0 bg-tbc-cream grain-texture"></div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero title — "The Home for Black Business", centered, with the
              word "Black" in gold. A short gold divider sits below it as a
              visual accent (matches the approved mockup). */}
          <div className="text-center mb-8 lg:mb-12">
            <h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl text-tbc-navy tracking-tight leading-tight"
              data-testid="v3-hero-preheading"
            >
              The Home for <span className="text-tbc-gold">Black</span> Business
            </h1>
            <span
              aria-hidden
              className="mt-4 inline-block h-1 w-16 rounded-full bg-tbc-gold"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-6 space-y-7 animate-fadeIn">
              <div className="space-y-4">
                <div className="flex justify-center lg:justify-start">
                  <span
                    className="inline-block px-4 py-2 bg-tbc-gold/20 text-tbc-navy font-medium text-sm tracking-wide rounded-full"
                    data-testid="v3-hero-eyebrow"
                  >
                    SUPPORT BLACK EXCELLENCE
                  </span>
                </div>
                <h1
                  className="font-heading text-3xl sm:text-4xl lg:text-5xl text-tbc-navy tracking-tight leading-tight"
                  data-testid="v3-hero-headline"
                >
                  List Your{" "}
                  <span className="text-tbc-gold">Black-Owned Business</span>{" "}
                  and Get Discovered
                </h1>
                <p className="text-lg text-gray-600 font-body max-w-xl" data-testid="v3-hero-subheadline">
                  Join The BLK Community to showcase your business, connect with new customers, and
                  grow with a marketplace built to celebrate and support Black-owned brands.
                </p>
              </div>

              {/* Primary + Secondary CTAs */}
              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={handleListBusiness}
                    className="bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-8 py-6 font-medium text-base"
                    data-testid="v3-hero-list-business"
                  >
                    List Your Business Free
                  </Button>
                  <Link to="/directory">
                    <Button
                      variant="outline"
                      className="border-2 border-tbc-navy text-tbc-navy hover:bg-tbc-navy hover:text-white rounded-full px-8 py-6 font-medium text-base w-full sm:w-auto"
                      data-testid="v3-hero-browse-directory"
                    >
                      Browse the Directory
                    </Button>
                  </Link>
                </div>
                <p className="text-sm text-gray-500 font-body" data-testid="v3-hero-microcopy">
                  No credit card required. Early businesses get founding-member perks.
                </p>
              </div>

              {/* Shopper search escape hatch */}
              <div className="space-y-2 max-w-xl pt-2">
                <p className="text-xs uppercase tracking-[0.2em] text-gray-500 font-medium">
                  Looking to shop instead?
                </p>
                <form onSubmit={handleSearch} className="space-y-3">
                  <div className="flex items-center bg-white rounded-full shadow-lg border border-gray-100 overflow-hidden">
                    <Search className="w-5 h-5 text-gray-400 ml-4" />
                    <input
                      type="text"
                      placeholder="Search businesses or products"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="flex-1 px-4 py-3 text-gray-700 font-body focus:outline-none"
                      data-testid="v3-hero-search-input"
                    />
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-1 flex items-center bg-white rounded-full shadow-lg border border-gray-100 overflow-hidden">
                      <MapPin className="w-5 h-5 text-gray-400 ml-4" />
                      <input
                        type="text"
                        placeholder="City, State, or ZIP"
                        value={locationQuery}
                        onChange={(e) => setLocationQuery(e.target.value)}
                        className="flex-1 px-4 py-3 text-gray-700 font-body focus:outline-none"
                        data-testid="v3-hero-location-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-6 font-medium"
                      data-testid="v3-hero-search-button"
                    >
                      Search
                    </Button>
                  </div>
                </form>
                <p className="text-sm text-gray-500 font-body pt-1">
                  Discover Black-owned businesses across growing categories and communities.
                </p>
              </div>
            </div>

            <div className="lg:col-span-6 animate-fadeIn stagger-2 opacity-0">
              <HeroCarousel />
            </div>
          </div>
        </div>
      </section>

      {/* ===================== BUILT FOR BUSINESS OWNERS ===================== */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <h2
              className="font-heading text-3xl sm:text-4xl text-tbc-navy mb-4"
              data-testid="v3-owners-heading"
            >
              Built for Business Owners
            </h2>
            <p className="text-gray-600 font-body text-lg">
              Everything you need to get seen, tell your story, and connect with customers who want
              to support Black-owned businesses.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              {
                icon: UserPlus,
                title: "Create Your Profile",
                text:
                  "Set up your business page with photos, your story, services, products, and contact details in just a few minutes.",
              },
              {
                icon: Eye,
                title: "Get Discovered",
                text:
                  "Show up in category browsing, featured sections, and customer searches from people actively looking to support businesses like yours.",
              },
              {
                icon: TrendingUp,
                title: "Grow Your Visibility",
                text:
                  "Build trust, stand out with strong visuals, and become part of a community-driven marketplace built for long-term growth.",
              },
            ].map(({ icon: Icon, title, text }, i) => (
              <div
                key={title}
                className={`group bg-tbc-cream/40 hover:bg-tbc-cream/70 transition-colors rounded-2xl p-8 animate-fadeIn stagger-${i + 1} opacity-0`}
                data-testid={`v3-owner-card-${i + 1}`}
              >
                <div className="w-14 h-14 bg-tbc-gold/20 rounded-xl flex items-center justify-center mb-5 group-hover:bg-tbc-gold/30 transition-colors">
                  <Icon className="w-7 h-7 text-tbc-gold" />
                </div>
                <h3 className="font-heading text-xl text-tbc-navy mb-2">{title}</h3>
                <p className="text-gray-600 font-body">{text}</p>
              </div>
            ))}
          </div>

          <div className="text-center space-y-3">
            <Button
              onClick={handleListBusiness}
              className="bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-10 py-6 font-medium text-lg"
              data-testid="v3-owners-cta"
            >
              Start Your Free Listing
            </Button>
            <p className="text-sm text-gray-500 font-body">
              Join as a founding business and help shape the community from day one.
            </p>
          </div>
        </div>
      </section>

      {/* ===================== HOW IT WORKS ===================== */}
      <section className="py-20 bg-tbc-cream grain-texture">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2
              className="font-heading text-3xl sm:text-4xl text-tbc-navy mb-3"
              data-testid="v3-how-heading"
            >
              How It Works
            </h2>
            <p className="text-gray-600 font-body text-lg">Getting started is simple.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                step: "1",
                title: "Create your profile",
                text:
                  "Add your business name, category, location, photos, and brand story.",
              },
              {
                step: "2",
                title: "Show what you offer",
                text:
                  "List your products, services, or specialties so customers know exactly what makes you unique.",
              },
              {
                step: "3",
                title: "Start getting discovered",
                text:
                  "Appear in the directory and connect with shoppers who want to support Black-owned businesses.",
              },
            ].map(({ step, title, text }, i) => (
              <div
                key={step}
                className={`relative bg-white rounded-2xl p-8 shadow-sm animate-fadeIn stagger-${i + 1} opacity-0`}
                data-testid={`v3-step-${step}`}
              >
                <div className="absolute -top-4 -left-2 w-12 h-12 bg-tbc-navy text-tbc-gold font-heading text-xl rounded-full flex items-center justify-center shadow-lg">
                  {step}
                </div>
                <h3 className="font-heading text-xl text-tbc-navy mb-2 pt-2">{title}</h3>
                <p className="text-gray-600 font-body">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== FOUNDING BUSINESS ===================== */}
      <section className="py-20 bg-tbc-navy">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-5">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-tbc-gold/15 text-tbc-gold font-medium text-xs tracking-[0.25em] rounded-full">
                <Sparkles className="w-4 h-4" />
                FOUNDING BUSINESSES
              </div>
              <h2
                className="font-heading text-3xl sm:text-4xl text-white"
                data-testid="v3-founding-heading"
              >
                Become a Founding Business
              </h2>
              <p className="text-gray-300 font-body text-lg">
                We&apos;re inviting the first 100 businesses to become Founding Members of The BLK
                Community and help build the largest platform dedicated to connecting and
                celebrating Black-owned businesses.
              </p>
              <div className="border-l-2 border-tbc-gold pl-4 py-2">
                <p className="text-tbc-gold font-heading text-base mb-1">
                  Pre-launch sale — limited-time founding offer.
                </p>
                <p className="text-gray-300 font-body text-sm">
                  Founding members get free visibility during the pre-launch phase + the first 30
                  days of paid features free at official launch. Official launch date coming soon.
                </p>
              </div>
              <p className="text-white/90 font-body font-medium">
                As a Founding Business, you&apos;ll receive:
              </p>
              <ul className="space-y-3 pt-1">
                {[
                  { icon: "⭐", text: "Founding Member badge displayed on your profile" },
                  { icon: "🚀", text: "Early visibility in the directory before public launch" },
                  { icon: "📍", text: "Priority consideration for featured placement" },
                  { icon: "💬", text: "Direct input on future features and platform improvements" },
                  { icon: "🎉", text: "Exclusive Founding Member networking events (virtual & in person)" },
                  { icon: "💰", text: "Discounts on future memberships, marketing services, merchandise, and partner offers" },
                  { icon: "📣", text: "Free publicity during the pre-launch phase across our newsletters, social media, and launch campaigns" },
                  { icon: "🏆", text: "Permanent recognition as one of the businesses that helped launch the community" },
                  { icon: "🎁", text: "First 30 days of paid features free at official launch (date coming soon)" },
                  { icon: "🔒", text: "$9.99/month founding rate locked in for life after your free trial" },
                ].map(({ icon, text }) => (
                  <li
                    key={text}
                    className="flex items-start gap-3 text-gray-100 font-body"
                    data-testid={`v3-founding-benefit-${text.toLowerCase().replace(/\s+/g, "-").slice(0, 40)}`}
                  >
                    <span className="text-xl leading-tight flex-shrink-0 w-6 text-center" aria-hidden="true">
                      {icon}
                    </span>
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
              <div className="pt-3 space-y-2">
                <p className="text-tbc-gold font-heading text-lg">
                  Only 100 Founding spots — first come, first served.
                </p>
                <Button
                  onClick={handleListBusiness}
                  className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full px-8 py-6 font-medium text-lg"
                  data-testid="v3-founding-cta"
                >
                  Claim Your Founding Spot
                </Button>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-3 bg-tbc-gold/20 rounded-3xl transform -rotate-3"></div>
                <img
                  src="https://images.unsplash.com/photo-1653566031535-bcf33e1c2893?w=900&q=80"
                  alt="Founding Black-owned businesses"
                  className="relative w-full h-80 object-cover rounded-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== FEATURED BUSINESSES ===================== */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Sponsored banner rotator — invisible if no active campaigns */}
          <div className="mb-10">
            <BannerAdRotator placement="home" />
          </div>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 mb-4">
            <div>
              <h2
                className="font-heading text-3xl sm:text-4xl text-tbc-navy mb-2"
                data-testid="v3-featured-heading"
              >
                Featured Businesses
              </h2>
              <p className="text-gray-600 font-body">
                Explore standout businesses making an impact in the community.
              </p>
            </div>
            <Link
              to="/directory"
              className="flex items-center gap-2 text-tbc-navy font-medium hover:text-tbc-gold transition-colors"
              data-testid="v3-view-all-businesses"
            >
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="text-sm text-tbc-gold font-medium tracking-wide mb-8 uppercase">
            Your business could be featured here.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(() => {
              const filtered = featuredBusinesses.filter((b) => !isExcludedFromFeatured(b));
              return filtered.length > 0 ? (
                filtered.map((business, index) => {
                const resolvedLogo = resolveLogoUrl(business.logo_url);
                const categoryLabel =
                  business.category === "Other" && business.custom_category
                    ? business.custom_category
                    : business.category;
                return (
                <Link
                  key={business.business_id}
                  to={`/business/${business.business_id}`}
                  className={`group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300 border border-gray-100 animate-fadeIn stagger-${(index % 3) + 1} opacity-0`}
                  data-testid={`v3-business-card-${business.business_id}`}
                >
                  <div
                    className="aspect-[4/5] relative bg-tbc-cream flex items-center justify-center p-8 overflow-hidden"
                    data-testid={`v3-featured-logo-${business.business_id}`}
                  >
                    <div className="absolute top-4 left-4 flex gap-2 z-10">
                      <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-xs font-medium text-tbc-navy rounded-full">
                        {categoryLabel}
                      </span>
                      {business.is_verified && (
                        <span className="px-2 py-1 bg-tbc-gold text-tbc-navy text-xs font-medium rounded-full flex items-center gap-1">
                          <BadgeCheck className="w-3 h-3" />
                        </span>
                      )}
                    </div>
                    {resolvedLogo ? (
                      <img
                        src={resolvedLogo}
                        alt={`${business.name} logo`}
                        loading="lazy"
                        className="max-w-[80%] max-h-[80%] w-auto h-auto object-contain transition-transform duration-500 group-hover:scale-105"
                        onError={(e) => {
                          // Hide broken image; reveal initials fallback sibling
                          e.currentTarget.style.display = "none";
                          const fallback = e.currentTarget.nextElementSibling;
                          if (fallback) fallback.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div
                      className="w-full h-full items-center justify-center font-heading font-bold tracking-wide text-tbc-navy text-6xl sm:text-7xl"
                      style={{ display: resolvedLogo ? "none" : "flex" }}
                      aria-label={`${business.name || "Business"} logo placeholder`}
                    >
                      <span>{initialsOf(business.name)}</span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-xl text-tbc-navy mb-1 group-hover:text-tbc-gold transition-colors">
                      {business.name}
                    </h3>
                    {business.bc_score > 0 && (
                      <div className="flex items-center gap-1 mb-2">
                        {Array.from({ length: 5 }, (_, i) => (
                          <Star
                            key={`star-${i}`}
                            className={`w-3 h-3 ${
                              i < Math.floor(business.bc_score)
                                ? "fill-tbc-gold text-tbc-gold"
                                : "text-gray-300"
                            }`}
                          />
                        ))}
                        <span className="text-xs text-gray-500 ml-1">{business.bc_score}/5</span>
                      </div>
                    )}
                    <p className="text-gray-600 text-sm font-body line-clamp-2">
                      {business.description}
                    </p>
                    {business.city && business.state && (
                      <p className="text-gray-400 text-xs mt-3 font-body flex items-center">
                        <MapPin className="w-3 h-3 mr-1" />
                        {business.city}, {business.state}
                      </p>
                    )}
                  </div>
                </Link>
                );
              })
            ) : (
              <div className="col-span-full text-center py-12">
                <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 font-body">
                  No businesses yet. Be the first to register!
                </p>
              </div>
            );
            })()}
          </div>
        </div>
      </section>

      {/* ===================== CATEGORY RAIL ===================== */}
      <CategoryRail />

      {/* ===================== HOW TBC WORKS (FOR SHOPPERS) ===================== */}
      <section className="py-20 bg-white" data-testid="how-tbc-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <p className="text-tbc-gold text-xs uppercase tracking-[0.25em] font-medium mb-3">
              For Shoppers
            </p>
            <h2
              className="font-heading text-3xl sm:text-4xl lg:text-5xl text-tbc-navy mb-4 leading-tight"
              data-testid="v3-shoppers-heading"
            >
              Shop Black. <span className="text-tbc-gold">Earn Rewards.</span> Build Community.
            </h2>
            <p className="text-gray-600 font-body text-lg leading-relaxed">
              The premier destination for discovering and supporting Black-owned businesses — with
              every purchase you make putting money back in your pocket.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Step 1 — Discover */}
            <div
              className="relative bg-tbc-cream/40 rounded-2xl p-6 border border-tbc-navy/5 hover:border-tbc-gold/40 transition-colors"
              data-testid="how-step-discover"
            >
              <div className="absolute -top-3 left-6 px-2.5 py-1 rounded-full bg-tbc-navy text-tbc-gold text-[10px] font-bold tracking-wider">
                STEP 01
              </div>
              <div className="w-12 h-12 rounded-xl bg-tbc-navy text-tbc-gold flex items-center justify-center mb-4 mt-2">
                <Compass className="w-6 h-6" />
              </div>
              <h3 className="font-heading text-xl text-tbc-navy mb-2">Discover</h3>
              <p className="text-sm text-gray-600 font-body leading-relaxed">
                Browse hundreds of Black-owned businesses across every category — fashion, food,
                beauty, home, services, and more. All verified. All in one place.
              </p>
            </div>

            {/* Step 2 — Shop */}
            <div
              className="relative bg-tbc-cream/40 rounded-2xl p-6 border border-tbc-navy/5 hover:border-tbc-gold/40 transition-colors"
              data-testid="how-step-shop"
            >
              <div className="absolute -top-3 left-6 px-2.5 py-1 rounded-full bg-tbc-navy text-tbc-gold text-[10px] font-bold tracking-wider">
                STEP 02
              </div>
              <div className="w-12 h-12 rounded-xl bg-tbc-navy text-tbc-gold flex items-center justify-center mb-4 mt-2">
                <ShoppingBag className="w-6 h-6" />
              </div>
              <h3 className="font-heading text-xl text-tbc-navy mb-2">Shop Directly</h3>
              <p className="text-sm text-gray-600 font-body leading-relaxed">
                Buy products and services straight through the platform. No redirects, no
                runaround. Secure checkout powered by Stripe.
              </p>
            </div>

            {/* Step 3 — Earn */}
            <div
              className="relative bg-tbc-cream/40 rounded-2xl p-6 border border-tbc-navy/5 hover:border-tbc-gold/40 transition-colors"
              data-testid="how-step-earn"
            >
              <div className="absolute -top-3 left-6 px-2.5 py-1 rounded-full bg-tbc-navy text-tbc-gold text-[10px] font-bold tracking-wider">
                STEP 03
              </div>
              <span
                className="absolute top-4 right-4 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-bold tracking-wider"
                data-testid="how-step-earn-live"
              >
                <Sparkles className="w-2.5 h-2.5" />
                Live
              </span>
              <div className="w-12 h-12 rounded-xl bg-tbc-navy text-tbc-gold flex items-center justify-center mb-4 mt-2">
                <Gift className="w-6 h-6" />
              </div>
              <h3 className="font-heading text-xl text-tbc-navy mb-2">Earn Points</h3>
              <p className="text-sm text-gray-600 font-body leading-relaxed">
                Every dollar you spend earns you <strong>TBC Points</strong> (1 pt per $1).
                100 points = $1 redeemable on future purchases. Redeem-at-checkout launches soon.
              </p>
            </div>

            {/* Step 4 — Membership */}
            <div
              className="relative bg-gradient-to-br from-tbc-navy to-[#0F2440] rounded-2xl p-6 text-white shadow-xl shadow-tbc-navy/20 ring-1 ring-tbc-gold/40"
              data-testid="how-step-member"
            >
              <div className="absolute -top-3 left-6 px-2.5 py-1 rounded-full bg-tbc-gold text-tbc-navy text-[10px] font-bold tracking-wider">
                STEP 04
              </div>
              <span
                className="absolute top-4 right-4 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-tbc-gold text-tbc-navy text-[10px] font-bold tracking-wider"
                data-testid="how-step-member-live"
              >
                <Crown className="w-2.5 h-2.5" />
                Live
              </span>
              <div className="w-12 h-12 rounded-xl bg-tbc-gold text-tbc-navy flex items-center justify-center mb-4 mt-2">
                <Crown className="w-6 h-6" />
              </div>
              <h3 className="font-heading text-xl text-white mb-2">TBC Member</h3>
              <p className="text-sm text-white/80 font-body leading-relaxed">
                Join TBC Member for just <strong className="text-tbc-gold">$9.99/month</strong> and
                earn <strong className="text-tbc-gold">double the points</strong> on every
                transaction. More support for the community. More rewards for you.
              </p>
            </div>
          </div>

          {/* Why + CTAs */}
          <div className="mt-16 max-w-3xl mx-auto text-center">
            <p className="text-gray-700 font-body text-lg leading-relaxed mb-8">
              When you shop through The Blk Community, your dollar doesn&apos;t just buy a product
              — it funds a dream, employs a neighbor, and strengthens a movement. Just community,
              commerce, and culture.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/directory">
                <Button
                  className="bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-8 py-6 font-medium text-base"
                  data-testid="v3-shoppers-cta"
                >
                  Browse Businesses
                </Button>
              </Link>
              <Button
                variant="outline"
                className="rounded-full border-tbc-gold text-tbc-navy hover:bg-tbc-gold hover:text-tbc-navy px-8 py-6 font-medium text-base"
                data-testid="v3-member-waitlist-cta"
                onClick={async () => {
                  // TBC Member checkout — $9.99/mo consumer subscription
                  try {
                    const res = await axios.post(
                      `${API}/tbc-member/checkout`,
                      { origin_url: window.location.origin },
                      { withCredentials: true }
                    );
                    if (res.data?.checkout_url) {
                      window.location.href = res.data.checkout_url;
                      return;
                    }
                  } catch (err) {
                    const status = err?.response?.status;
                    const detail = err?.response?.data?.detail;
                    if (status === 401) {
                      const { toast } = await import("sonner");
                      toast.info("Sign in first to join as a TBC Member.");
                      // Fall back to scrolling to footer newsletter as a "save my spot" path
                      const footer = document.querySelector("footer");
                      if (footer) footer.scrollIntoView({ behavior: "smooth" });
                      return;
                    }
                    const { toast } = await import("sonner");
                    toast.error(detail || "Couldn't start TBC Member checkout.");
                  }
                }}
              >
                <Crown className="w-4 h-4 mr-2" />
                Join TBC Member · $9.99/mo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== CATEGORIES GRID ===================== */}
      <section className="py-20 bg-tbc-cream grain-texture">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <h2
              className="font-heading text-3xl sm:text-4xl text-tbc-navy mb-3"
              data-testid="v3-category-heading"
            >
              Explore Categories
            </h2>
            <p className="text-gray-700 font-body text-lg mb-1">
              Find exactly what you&apos;re looking for across a growing range of Black-owned
              businesses.
            </p>
            <p className="text-gray-500 font-body text-sm">
              Browse by category and discover brands, services, and creators in just a few taps.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {categories.slice(0, 10).map((category, index) => {
              const heroSlug = slugForCategory(category);
              const to = heroSlug
                ? `/category/${heroSlug}`
                : `/directory?category=${encodeURIComponent(category)}`;
              return (
                <Link
                  key={category}
                  to={to}
                  className={`group relative overflow-hidden rounded-xl aspect-square animate-fadeIn stagger-${(index % 5) + 1} opacity-0`}
                  data-testid={`v3-category-${category.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <img
                    src={CATEGORY_IMAGES[category] || CATEGORY_IMAGES["Other"]}
                    alt={category}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-tbc-navy/80 via-tbc-navy/20 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-white font-body font-medium text-sm tracking-wide">
                      {category}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== COMMUNITY FIRST ===================== */}
      <section className="py-20 bg-tbc-cream grain-texture">
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-5">
            <h2
              className="font-heading text-3xl sm:text-4xl text-tbc-navy"
              data-testid="v3-community-heading"
            >
              Community First
            </h2>
            <p className="text-gray-700 font-body text-lg">
              We believe stronger communities are built when we intentionally support one another.
              The BLK Community makes it easier to discover, support, and celebrate Black-owned
              businesses every day.
            </p>

            <div className="pt-2">
              <p className="font-heading text-xl text-tbc-navy mb-2">Building Community</p>
              <p className="text-gray-600 font-body">
                A growing network of entrepreneurs, creators, and shoppers coming together through
                visibility, collaboration, and support.
              </p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 bg-tbc-gold/20 rounded-3xl transform rotate-3"></div>
              <img
                src="https://images.unsplash.com/photo-1739298061709-cfc57e6b620e?w=900&q=80"
                alt="Black community coming together"
                className="relative w-full h-72 object-cover rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Meet the Founder section now lives on its own page at /founder (linked from the navbar) */}


      {/* ===================== MID-PAGE CTA ===================== */}
      <section className="py-20 bg-tbc-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="font-heading text-3xl sm:text-4xl text-white mb-5"
            data-testid="v3-mid-cta-heading"
          >
            Own a Black-Owned Business?
          </h2>
          <p className="text-gray-300 font-body text-lg mb-8 max-w-2xl mx-auto">
            Join The BLK Community and connect with customers who are actively looking to discover
            and support businesses like yours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={handleListBusiness}
              className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full px-8 py-6 font-medium text-lg"
              data-testid="v3-mid-cta-primary"
            >
              Get Started Free
            </Button>
            <Link to="/directory">
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-tbc-navy rounded-full px-8 py-6 font-medium text-lg"
                data-testid="v3-mid-cta-secondary"
              >
                Browse Directory
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ===================== WHY JOIN ===================== */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2
              className="font-heading text-3xl sm:text-4xl text-tbc-navy mb-3"
              data-testid="v3-why-heading"
            >
              Why Join The BLK Community?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Target,
                title: "Purpose-driven visibility",
                text:
                  "Put your business in front of people who intentionally want to support Black-owned brands.",
              },
              {
                icon: Compass,
                title: "Community-powered discovery",
                text:
                  "Be part of a marketplace where shoppers can search, browse, and discover businesses across multiple categories.",
              },
              {
                icon: HeartHandshake,
                title: "A platform built to grow with you",
                text:
                  "From directory exposure to featured placement and future growth opportunities, we’re building with long-term community impact in mind.",
              },
            ].map(({ icon: Icon, title, text }, i) => (
              <div
                key={title}
                className={`text-center p-8 rounded-2xl bg-tbc-cream/50 animate-fadeIn stagger-${i + 1} opacity-0`}
                data-testid={`v3-why-card-${i + 1}`}
              >
                <div className="w-16 h-16 bg-tbc-gold/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Icon className="w-8 h-8 text-tbc-gold" />
                </div>
                <h3 className="font-heading text-xl text-tbc-navy mb-3">{title}</h3>
                <p className="text-gray-600 font-body">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== FOOTER CTA ===================== */}
      <section className="py-20 bg-tbc-cream grain-texture">
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Users className="w-12 h-12 text-tbc-gold mx-auto mb-5" />
          <h2
            className="font-heading text-3xl sm:text-4xl text-tbc-navy mb-4"
            data-testid="v3-footer-cta-heading"
          >
            Ready to Grow Your Visibility?
          </h2>
          <p className="text-gray-700 font-body text-lg mb-8">
            Create your free business listing and join a marketplace designed to spotlight
            Black-owned businesses.
          </p>
          <Button
            onClick={handleListBusiness}
            className="bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-10 py-6 font-medium text-lg"
            data-testid="v3-footer-cta-button"
          >
            List Your Business Free
          </Button>
        </div>
      </section>

      <Footer />
      <VersionSwitcher />
    </div>
  );
}
